/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { VSAOI_STATUS_OPTIONS, DISABILITY_OPTIONS } from '../../lib/workStatus';

const EMPTY_FORM = {
  name: '',
  username: '',
  email: '',
  password: '',
  tax_book_submitted: false,
  dependents: 0,
  vsaoi_status: 'regular',
  disability_group: 'none',
  repressed: false,
  employment_start: '',
  employment_end: ''
};

const EmployeesManagement = () => {
  const { t } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const fetchEmployees = async () => {
    try {
      const data = await getEmployees();
      setEmployees(data || []);
    } catch (error) {
      toast.error(t('portalUi.admin.employees.toastLoadError', 'Neizdevās ielādēt darbiniekus'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const openModal = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        name: employee.name || '',
        username: employee.username || '',
        email: employee.email || '',
        password: '',
        tax_book_submitted: Boolean(employee.tax_book_submitted),
        dependents: employee.dependents ?? 0,
        vsaoi_status: employee.vsaoi_status || 'regular',
        disability_group: employee.disability_group || 'none',
        repressed: Boolean(employee.repressed),
        employment_start: employee.employment_start ? employee.employment_start.split('T')[0] : '',
        employment_end: employee.employment_end ? employee.employment_end.split('T')[0] : ''
      });
    } else {
      setEditingEmployee(null);
      setFormData({ ...EMPTY_FORM });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.username || !formData.email) {
      toast.error(t('portalUi.admin.employees.toastValidation', 'Aizpildiet obligātos laukus'));
      return;
    }
    if (!editingEmployee && formData.password.length < 8) {
      toast.error(t('portalUi.admin.employees.toastPassword', 'Parolei jābūt vismaz 8 simbolu garai'));
      return;
    }
    const taxPayload = {
      tax_book_submitted: formData.tax_book_submitted,
      dependents: Number(formData.dependents || 0),
      vsaoi_status: formData.vsaoi_status,
      disability_group: formData.disability_group,
      repressed: formData.repressed,
      employment_start: formData.employment_start || null,
      employment_end: formData.employment_end || null
    };
    try {
      if (editingEmployee) {
        const { password, ...rest } = formData;
        await updateEmployee(editingEmployee.id, { name: rest.name, username: rest.username, email: rest.email, ...(password ? { password } : {}), ...taxPayload });
        toast.success(t('portalUi.admin.employees.toastUpdated', 'Darbinieks atjaunināts'));
      } else {
        const created = await createEmployee({ name: formData.name, username: formData.username, email: formData.email, password: formData.password });
        await updateEmployee(created.id, taxPayload);
        toast.success(t('portalUi.admin.employees.toastCreated', 'Darbinieks pievienots'));
      }
      setIsModalOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.employees.toastError', 'Kļūda saglabājot darbinieku'));
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm(t('portalUi.admin.employees.confirmDelete', 'Vai tiešām dzēst darbinieku?'))) return;
    try {
      await deleteEmployee(employeeId);
      toast.success(t('portalUi.admin.employees.toastDeleted', 'Darbinieks dzēsts'));
      fetchEmployees();
    } catch (error) {
      toast.error(t('portalUi.admin.employees.toastDeleteError', 'Neizdevās dzēst darbinieku'));
    }
  };

  const filteredEmployees = employees.filter((employee) =>
    employee.name.toLowerCase().includes(search.toLowerCase()) ||
    employee.username.toLowerCase().includes(search.toLowerCase()) ||
    employee.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-employees-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="admin-employees-title">
            {t('portalUi.admin.employees.title', 'Darbinieki')}
          </h1>
          <p className="text-[#2E2E2E]/70" data-testid="admin-employees-subtitle">
            {t('portalUi.admin.employees.subtitle', 'Pārvaldiet darbinieku piekļuves')}
          </p>
        </div>
        <Button onClick={() => openModal()} data-testid="admin-employees-add">
          {t('portalUi.admin.employees.add', 'Pievienot darbinieku')}
        </Button>
      </div>

      <div className="max-w-md">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('portalUi.admin.employees.search', 'Meklēt darbiniekus...')}
          data-testid="admin-employees-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48" data-testid="admin-employees-loading">
          <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-employees-table">
                <thead className="bg-[#f8faf8]">
                  <tr>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employees.table.name', 'Vārds')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employees.table.username', 'Lietotājvārds')}</th>
                    <th className="text-left px-6 py-4">{t('portalUi.admin.employees.table.email', 'E-pasts')}</th>
                    <th className="text-right px-6 py-4">{t('portalUi.admin.employees.table.actions', 'Darbības')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-[#f8faf8] transition-colors" data-testid={`admin-employee-row-${employee.id}`}>
                      <td className="px-6 py-4" data-testid={`admin-employee-name-${employee.id}`}>{employee.name}</td>
                      <td className="px-6 py-4" data-testid={`admin-employee-username-${employee.id}`}>{employee.username}</td>
                      <td className="px-6 py-4" data-testid={`admin-employee-email-${employee.id}`}>{employee.email || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openModal(employee)} data-testid={`admin-employee-edit-${employee.id}`}>
                            {t('portalUi.admin.employees.edit', 'Rediģēt')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(employee.id)} data-testid={`admin-employee-delete-${employee.id}`}>
                            {t('portalUi.admin.employees.delete', 'Dzēst')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredEmployees.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-6 text-center text-[#2E2E2E]/60" data-testid="admin-employees-empty">
                        {t('portalUi.admin.employees.empty', 'Nav pievienotu darbinieku')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="admin-employee-modal-title">
              {editingEmployee ? t('portalUi.admin.employees.editTitle', 'Rediģēt darbinieku') : t('portalUi.admin.employees.addTitle', 'Jauns darbinieks')}
            </DialogTitle>
            <DialogDescription className="sr-only" data-testid="admin-employee-modal-desc">
              {t('portalUi.admin.employees.modalDescription', 'Ievadiet darbinieka informāciju')}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label data-testid="admin-employee-name-label">{t('portalUi.admin.employees.form.name', 'Vārds')}</Label>
              <Input value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} data-testid="admin-employee-name" />
            </div>
            <div className="space-y-2">
              <Label data-testid="admin-employee-username-label">{t('portalUi.admin.employees.form.username', 'Lietotājvārds')}</Label>
              <Input value={formData.username} onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))} data-testid="admin-employee-username" />
            </div>
            <div className="space-y-2">
              <Label data-testid="admin-employee-email-label">{t('portalUi.admin.employees.form.email', 'E-pasts')}</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} data-testid="admin-employee-email" required />
            </div>
            <div className="space-y-2">
              <Label data-testid="admin-employee-password-label">{editingEmployee ? t('portalUi.admin.employees.form.passwordEdit', 'Jauna parole (neobligāti)') : t('portalUi.admin.employees.form.password', 'Parole')}</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))} data-testid="admin-employee-password" />
            </div>
            <div className="rounded-xl border border-[#1F3D2B]/15 bg-[#f8faf8] p-3 space-y-3" data-testid="admin-employee-tax-section">
              <p className="text-sm font-semibold text-[#1F3D2B]">Nodokļu iestatījumi (algas aprēķinam)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <Checkbox checked={formData.tax_book_submitted} onCheckedChange={(v) => setFormData((prev) => ({ ...prev, tax_book_submitted: Boolean(v) }))} data-testid="admin-employee-tax-book" />
                  Algas nodokļa grāmatiņa iesniegta pie uzņēmuma
                </label>
                <div className="space-y-1">
                  <Label className="text-xs">Apgādājamo skaits</Label>
                  <Input type="number" min="0" value={formData.dependents} onChange={(e) => setFormData((prev) => ({ ...prev, dependents: e.target.value }))} data-testid="admin-employee-dependents" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">VSAOI statuss</Label>
                  <Select value={formData.vsaoi_status} onValueChange={(v) => setFormData((prev) => ({ ...prev, vsaoi_status: v }))}>
                    <SelectTrigger data-testid="admin-employee-vsaoi-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{VSAOI_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`admin-employee-vsaoi-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Invaliditātes atvieglojums</Label>
                  <Select value={formData.disability_group} onValueChange={(v) => setFormData((prev) => ({ ...prev, disability_group: v }))}>
                    <SelectTrigger data-testid="admin-employee-disability"><SelectValue /></SelectTrigger>
                    <SelectContent>{DISABILITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`admin-employee-disability-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={formData.repressed} onCheckedChange={(v) => setFormData((prev) => ({ ...prev, repressed: Boolean(v) }))} data-testid="admin-employee-repressed" />
                  Politiski represētā persona / pretošanās kustības dalībnieks
                </label>
                <div className="space-y-1">
                  <Label className="text-xs">Darba attiecību sākums</Label>
                  <Input type="date" value={formData.employment_start} onChange={(e) => setFormData((prev) => ({ ...prev, employment_start: e.target.value }))} data-testid="admin-employee-employment-start" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Darba attiecību beigas</Label>
                  <Input type="date" value={formData.employment_end} onChange={(e) => setFormData((prev) => ({ ...prev, employment_end: e.target.value }))} data-testid="admin-employee-employment-end" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)} data-testid="admin-employee-cancel">
                {t('portalUi.admin.employees.cancel', 'Atcelt')}
              </Button>
              <Button type="submit" data-testid="admin-employee-save">
                {editingEmployee ? t('portalUi.admin.employees.save', 'Saglabāt') : t('portalUi.admin.employees.create', 'Izveidot')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesManagement;
